import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { getSuperAdminDashboard } from "../../api/superAdminApi";
import { deleteUser } from "../../api/usersApi";
import { AuthContext } from "../../Contexts/AuthContext";
import {
  FiUsers,
  FiSearch,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiMail,
  FiCalendar,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiTrash2,
  FiLoader,
  FiActivity,
  FiTrendingUp,
} from "react-icons/fi";
import { GiCow, GiBarn } from "react-icons/gi";
import { FaUsersCog, FaUserTie, FaUserCheck } from "react-icons/fa";
import { TbGenderBigender } from "react-icons/tb";
import swal from "sweetalert";
import "./superAdmin.css";

export default function SuperAdminDashboard() {
  const { t } = useTranslation();
  const { user: authUser } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedFerme, setExpandedFerme] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getSuperAdminDashboard();
      setData(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const confirmed = await swal({
      title: t("superAdmin.confirmDelete"),
      text: `${userName}`,
      icon: "warning",
      buttons: [t("superAdmin.cancel"), t("superAdmin.delete")],
      dangerMode: true,
    });

    if (confirmed) {
      try {
        await deleteUser(userId);
        fetchData();
        swal(t("superAdmin.deleted"), "", "success");
      } catch {
        swal(t("superAdmin.error"), "", "error");
      }
    }
  };

  const toggleFerme = (id) => {
    setExpandedFerme(expandedFerme === id ? null : id);
  };

  if (loading) {
    return (
      <div className="sa-loading">
        <FiLoader className="sa-spinner" />
        <p>{t("superAdmin.loading")}</p>
      </div>
    );
  }

  if (!data) return null;

  const { stats, fermes, recentUsers } = data;
  const farmsWithoutAdmins = fermes.filter((ferme) => ferme.admins_count === 0).length;
  const emptyFarms = fermes.filter(
    (ferme) => ferme.admins_count + ferme.eleveurs_count === 0
  ).length;
  const farmsNeedAttention = fermes.filter(
    (ferme) => ferme.admins_count === 0 || ferme.eleveurs_count === 0
  ).length;
  const verifiedRecentUsers = recentUsers.filter((user) => user.email_verified_at).length;
  const recentAdmins = recentUsers.filter((user) => user.role === "admin").length;
  const recentFarmers = recentUsers.filter((user) => user.role === "eleveur").length;
  const averageAnimalsPerFarm = stats.totalFermes
    ? (stats.totalAnimals / stats.totalFermes).toFixed(1)
    : "0";
  const largestFarm = fermes.reduce((largest, ferme) => {
    if (!largest || ferme.animals_count > largest.animals_count) {
      return ferme;
    }
    return largest;
  }, null);

  const filteredFermes = fermes.filter((ferme) => {
    const query = search.toLowerCase();
    const nameMatch = ferme.nom?.toLowerCase().includes(query);
    const adresseMatch = ferme.adresse?.toLowerCase().includes(query);
    const adminMatch = ferme.admins?.some(
      (admin) =>
        admin.nom?.toLowerCase().includes(query) ||
        admin.email?.toLowerCase().includes(query)
    );
    const farmerMatch = ferme.eleveurs?.some(
      (eleveur) =>
        eleveur.nom?.toLowerCase().includes(query) ||
        eleveur.email?.toLowerCase().includes(query)
    );

    const matchesSearch = !query || nameMatch || adresseMatch || adminMatch || farmerMatch;

    const matchesFilter =
      filter === "all" ||
      (filter === "admin" && ferme.admins_count > 0) ||
      (filter === "eleveur" && ferme.eleveurs_count > 0) ||
      (filter === "attention" &&
        (ferme.admins_count === 0 || ferme.eleveurs_count === 0));

    return matchesSearch && matchesFilter;
  });

  const statCards = [
    {
      icon: <GiBarn />,
      label: t("superAdmin.stats.farms"),
      value: stats.totalFermes,
      color: "green",
      helper: t("superAdmin.directorySummary"),
    },
    {
      icon: <FiUsers />,
      label: t("superAdmin.stats.totalUsers"),
      value: stats.totalUsers,
      color: "blue",
      helper: t("superAdmin.managedAccounts"),
    },
    {
      icon: <FaUserTie />,
      label: t("superAdmin.stats.admins"),
      value: stats.totalAdmins,
      color: "amber",
      helper: t("superAdmin.governance"),
    },
    {
      icon: <FaUserCheck />,
      label: t("superAdmin.stats.eleveurs"),
      value: stats.totalEleveurs,
      color: "teal",
      helper: t("superAdmin.operations"),
    },
    {
      icon: <GiCow />,
      label: t("superAdmin.stats.animals"),
      value: stats.totalAnimals,
      color: "purple",
      helper: t("superAdmin.avgAnimalsPerFarm"),
      accentValue: averageAnimalsPerFarm,
    },
    {
      icon: <FiActivity />,
      label: t("superAdmin.stats.reproductions"),
      value: stats.totalRepros,
      color: "rose",
      helper: t("superAdmin.farmsNeedAttention"),
      accentValue: farmsNeedAttention,
    },
  ];

  const platformSignals = [
    {
      icon: <FaUsersCog />,
      label: t("superAdmin.managedAccounts"),
      value: stats.totalAdmins + stats.totalEleveurs,
      tone: "blue",
    },
    {
      icon: <FiCheckCircle />,
      label: t("superAdmin.verified"),
      value: `${verifiedRecentUsers}/${recentUsers.length || 0}`,
      tone: "green",
    },
    {
      icon: <FiAlertCircle />,
      label: t("superAdmin.farmsNeedAttention"),
      value: farmsNeedAttention,
      tone: "amber",
    },
  ];

  const watchlistItems = [
    {
      icon: <FiAlertCircle />,
      label: t("superAdmin.farmsWithoutAdmins"),
      value: farmsWithoutAdmins,
      tone: farmsWithoutAdmins ? "alert" : "ok",
    },
    {
      icon: <GiBarn />,
      label: t("superAdmin.emptyFarms"),
      value: emptyFarms,
      tone: emptyFarms ? "alert" : "ok",
    },
    {
      icon: <GiCow />,
      label: t("superAdmin.largestFarm"),
      value: largestFarm?.nom || "-",
      caption: largestFarm ? `${largestFarm.animals_count} ${t("superAdmin.animal")}` : null,
      tone: "neutral",
    },
  ];

  return (
    <div className="sa-page">
      <div className="sa-backdrop sa-backdrop--one" />
      <div className="sa-backdrop sa-backdrop--two" />

      <section className="sa-hero">
        <div className="sa-hero-main">
          <div className="sa-header-badge sa-header-badge--hero">
            <FiShield />
            <span>Super Admin</span>
          </div>

          <div className="sa-header">
            <div className="sa-header-left">
              <div className="sa-header-icon">
                <FiShield />
              </div>
              <div>
                <h1 className="sa-title">{t("superAdmin.title")}</h1>
                <p className="sa-subtitle">{t("superAdmin.subtitle")}</p>
              </div>
            </div>
          </div>

          <div className="sa-hero-copy">
            <p>{t("superAdmin.platformPulse")}</p>
            <div className="sa-hero-metrics">
              <div className="sa-hero-metric">
                <span className="sa-hero-metric-label">{t("superAdmin.avgAnimalsPerFarm")}</span>
                <strong>{averageAnimalsPerFarm}</strong>
              </div>
              <div className="sa-hero-metric">
                <span className="sa-hero-metric-label">{t("superAdmin.newAdmins")}</span>
                <strong>{recentAdmins}</strong>
              </div>
              <div className="sa-hero-metric">
                <span className="sa-hero-metric-label">{t("superAdmin.newFarmers")}</span>
                <strong>{recentFarmers}</strong>
              </div>
            </div>
          </div>
        </div>

        <aside className="sa-hero-side">
          <div className="sa-panel-kicker">{t("superAdmin.platformPulse")}</div>
          <div className="sa-pulse-score-wrap">
            <span className="sa-pulse-score">{stats.totalFermes}</span>
            <span className="sa-pulse-caption">{t("superAdmin.stats.farms")}</span>
          </div>

          <div className="sa-pulse-list">
            {platformSignals.map((signal) => (
              <div className={`sa-pulse-item sa-pulse-item--${signal.tone}`} key={signal.label}>
                <span className="sa-pulse-icon">{signal.icon}</span>
                <div>
                  <span className="sa-pulse-label">{signal.label}</span>
                  <strong className="sa-pulse-value">{signal.value}</strong>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </section>

      <div className="sa-stats">
        {statCards.map((card) => (
          <div className={`sa-stat sa-stat--${card.color}`} key={card.label}>
            <div className="sa-stat-top">
              <div className="sa-stat-icon">{card.icon}</div>
              {card.accentValue ? <span className="sa-stat-accent">{card.accentValue}</span> : null}
            </div>
            <div className="sa-stat-body">
              <span className="sa-stat-value">{card.value}</span>
              <span className="sa-stat-label">{card.label}</span>
              <span className="sa-stat-helper">{card.helper}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="sa-layout">
        <div className="sa-main-column">
          <section className="sa-section sa-section--panel">
            <div className="sa-section-header">
              <div>
                <span className="sa-section-kicker">{t("superAdmin.governance")}</span>
                <h2>{t("superAdmin.recentUsers")}</h2>
              </div>
              <span className="sa-section-badge">
                <FiTrendingUp />
                {recentUsers.length}
              </span>
            </div>

            <div className="sa-recent-grid">
              {recentUsers.map((recentUser) => {
                const isVerified = Boolean(recentUser.email_verified_at);

                return (
                  <div className="sa-recent-card" key={recentUser.id}>
                    <div className="sa-recent-top">
                      <div className="sa-recent-avatar">
                        {recentUser.nom?.charAt(0).toUpperCase()}
                      </div>
                      <div className="sa-recent-info">
                        <span className="sa-recent-name">{recentUser.nom}</span>
                        <span className="sa-recent-email">{recentUser.email || "-"}</span>
                      </div>
                    </div>

                    <div className="sa-recent-meta">
                      <span className={`sa-role-chip sa-role-chip--${recentUser.role}`}>
                        {recentUser.role}
                      </span>
                      <span
                        className={`sa-status-pill ${
                          isVerified ? "sa-status-pill--verified" : "sa-status-pill--pending"
                        }`}
                      >
                        {isVerified ? <FiCheckCircle /> : <FiAlertCircle />}
                        {isVerified
                          ? t("superAdmin.verified")
                          : t("superAdmin.pendingVerification")}
                      </span>
                    </div>

                    <div className="sa-recent-footer">
                      <span>
                        <FiCalendar />
                        {new Date(recentUser.created_at).toLocaleDateString()}
                      </span>
                      {recentUser.ferme?.nom ? <span>{recentUser.ferme.nom}</span> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="sa-section sa-section--panel">
            <div className="sa-section-header">
              <div>
                <span className="sa-section-kicker">{t("superAdmin.operations")}</span>
                <h2>{t("superAdmin.farmsOverview")}</h2>
              </div>
              <span className="sa-section-badge">{filteredFermes.length}</span>
            </div>

            <div className="sa-toolbar">
              <div className="sa-search">
                <FiSearch className="sa-search-icon" />
                <input
                  type="text"
                  placeholder={t("superAdmin.searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search ? (
                  <button className="sa-search-clear" onClick={() => setSearch("")}>
                    <FiX />
                  </button>
                ) : null}
              </div>

              <div className="sa-filter-group">
                {["all", "admin", "eleveur", "attention"].map((filterKey) => (
                  <button
                    type="button"
                    key={filterKey}
                    className={`sa-filter-pill ${filter === filterKey ? "is-active" : ""}`}
                    onClick={() => setFilter(filterKey)}
                  >
                    {t(`superAdmin.filters.${filterKey}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="sa-farms">
              {filteredFermes.length === 0 ? (
                <div className="sa-empty">
                  <GiBarn />
                  <p>{t("superAdmin.noFarms")}</p>
                </div>
              ) : (
                filteredFermes.map((ferme) => {
                  const isExpanded = expandedFerme === ferme.id;
                  const allUsers = [...(ferme.admins || []), ...(ferme.eleveurs || [])];
                  const farmNeedsAttention =
                    ferme.admins_count === 0 || ferme.eleveurs_count === 0;

                  return (
                    <article
                      className={`sa-farm-card ${isExpanded ? "sa-farm-card--open" : ""}`}
                      key={ferme.id}
                    >
                      <div className="sa-farm-top" onClick={() => toggleFerme(ferme.id)}>
                        <div className="sa-farm-top-left">
                          <div className="sa-farm-icon-wrap">
                            <GiBarn />
                          </div>
                          <div className="sa-farm-info">
                            <div className="sa-farm-title-row">
                              <h3 className="sa-farm-name">{ferme.nom}</h3>
                              <span
                                className={`sa-status-pill ${
                                  farmNeedsAttention
                                    ? "sa-status-pill--pending"
                                    : "sa-status-pill--verified"
                                }`}
                              >
                                {farmNeedsAttention ? <FiAlertCircle /> : <FiCheckCircle />}
                                {farmNeedsAttention
                                  ? t("superAdmin.needsAttention")
                                  : t("superAdmin.healthy")}
                              </span>
                            </div>
                            {ferme.adresse ? (
                              <span className="sa-farm-addr">{ferme.adresse}</span>
                            ) : null}
                          </div>
                        </div>

                        <div className="sa-farm-stats-mini">
                          <span className="sa-farm-mini">
                            <FiUsers /> {ferme.admins_count + ferme.eleveurs_count} {t("superAdmin.members")}
                          </span>
                          <span className="sa-farm-mini">
                            <GiCow /> {ferme.animals_count} {t("superAdmin.animal")}
                          </span>
                        </div>

                        <div className="sa-farm-toggle">
                          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                        </div>
                      </div>

                      {isExpanded ? (
                        <div className="sa-farm-expand">
                          <div className="sa-farm-detail-stats">
                            <div className="sa-detail-chip">
                              <FaUserTie />
                              <span>
                                {ferme.admins_count} {t("superAdmin.admin")}
                              </span>
                            </div>
                            <div className="sa-detail-chip">
                              <FaUserCheck />
                              <span>
                                {ferme.eleveurs_count} {t("superAdmin.eleveur")}
                              </span>
                            </div>
                            <div className="sa-detail-chip">
                              <GiCow />
                              <span>
                                {ferme.animals_count} {t("superAdmin.animal")}
                              </span>
                            </div>
                          </div>

                          <div className="sa-farm-grid">
                            <div className="sa-panel-card">
                              <div className="sa-panel-card-title">{t("superAdmin.managedAccounts")}</div>
                              <div className="sa-users-list">
                                {allUsers.length === 0 ? (
                                  <p className="sa-no-users">{t("superAdmin.noUsers")}</p>
                                ) : (
                                  allUsers.map((farmUser) => (
                                    <div className="sa-user-row" key={farmUser.id}>
                                      <div className="sa-user-avatar">
                                        {farmUser.nom?.charAt(0).toUpperCase()}
                                      </div>
                                      <div className="sa-user-info">
                                        <span className="sa-user-name">{farmUser.nom}</span>
                                        <span className="sa-user-email">
                                          <FiMail /> {farmUser.email || "-"}
                                        </span>
                                      </div>
                                      <span className={`sa-role-chip sa-role-chip--${farmUser.role}`}>
                                        {farmUser.role}
                                      </span>
                                      <span className="sa-user-date">
                                        <FiCalendar />
                                        {new Date(farmUser.created_at).toLocaleDateString()}
                                      </span>
                                      {farmUser.role !== "superadmin" && authUser?.id !== farmUser.id ? (
                                        <button
                                          className="sa-delete-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteUser(farmUser.id, farmUser.nom);
                                          }}
                                        >
                                          <FiTrash2 />
                                        </button>
                                      ) : null}
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>

                            <div className="sa-panel-card">
                              <div className="sa-panel-card-title">{t("superAdmin.animalsPreview")}</div>
                              {ferme.animals && ferme.animals.length > 0 ? (
                                <div className="sa-animals-preview sa-animals-preview--no-border">
                                  <div className="sa-animal-chips">
                                    {ferme.animals.slice(0, 8).map((animal) => (
                                      <span className="sa-animal-chip" key={animal.id}>
                                        <GiCow />
                                        {animal.nom || animal.numero}
                                        <TbGenderBigender
                                          className={`sa-gender sa-gender--${animal.genre}`}
                                        />
                                      </span>
                                    ))}
                                    {ferme.animals.length > 8 ? (
                                      <span className="sa-animal-chip sa-animal-chip--more">
                                        +{ferme.animals.length - 8}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              ) : (
                                <p className="sa-no-users">{t("superAdmin.noUsers")}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </article>
                  );
                })
              )}
            </div>
          </section>
        </div>

        <aside className="sa-side-column">
          <section className="sa-section sa-section--panel">
            <div className="sa-section-header">
              <div>
                <span className="sa-section-kicker">{t("superAdmin.operations")}</span>
                <h2>{t("superAdmin.platformPulse")}</h2>
              </div>
            </div>

            <div className="sa-watchlist">
              {watchlistItems.map((item) => (
                <div className={`sa-watch-item sa-watch-item--${item.tone}`} key={item.label}>
                  <span className="sa-watch-icon">{item.icon}</span>
                  <div className="sa-watch-content">
                    <span className="sa-watch-label">{item.label}</span>
                    <strong className="sa-watch-value">{item.value}</strong>
                    {item.caption ? <span className="sa-watch-caption">{item.caption}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
